import React, { useState } from 'react';
import adminAttendanceService from '../../firebase/adminAttendanceService';

/**
 * AttendanceTable Component
 * Displays attendance records in table format with expandable rows
 */
const AttendanceTable = ({ 
  records, 
  onStatusUpdate, 
  onPhotoView, 
  onRefresh,
  currentPage,
  recordsPerPage,
  totalRecords 
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [updating, setUpdating] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  // Toggle row expansion
  const toggleRowExpansion = (recordId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
    }
    setExpandedRows(newExpanded);
  };

  // Handle status update
  const handleStatusUpdate = async (recordId, newStatus) => {
    if (updating.has(recordId)) return;

    try {
      const newUpdating = new Set(updating);
      newUpdating.add(recordId);
      setUpdating(newUpdating);

      const result = await adminAttendanceService.updateAttendanceStatus(recordId, newStatus);
      
      if (result.success) {
        onStatusUpdate && onStatusUpdate(recordId, newStatus);
        onRefresh && onRefresh();
      } else {
        alert(`Failed to update status: ${result.error}`);
      }
    } catch (error) {
      console.error('Error updating attendance status:', error);
      alert('Failed to update attendance status');
    } finally {
      const newUpdating = new Set(updating);
      newUpdating.delete(recordId);
      setUpdating(newUpdating);
    }
  };

  // Handle photo click with enhanced error handling and fallback
  const handlePhotoClick = async (record) => {
    if (!record.photoUrl) {
      alert('No photo available for this attendance record.');
      return;
    }

    let loadingToast = null;

    try {
      // Show loading state
      loadingToast = document.createElement('div');
      loadingToast.textContent = '📷 Loading photo...';
      loadingToast.style.cssText = `
        position: fixed; top: 20px; right: 20px; z-index: 10000;
        background: #1890ff; color: white; padding: 12px 20px;
        border-radius: 6px; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      `;
      loadingToast.id = 'photo-loading-toast';
      document.body.appendChild(loadingToast);

      // Try to get secure signed URL with fallback mechanism
      let photoUrl;
      try {
        console.log('🔄 Attempting to get signed URL for attendance:', record.id);
        photoUrl = await adminAttendanceService.getPhotoWithFallback(record.id, record.photoUrl);
      } catch (signedUrlError) {
        console.log('⚠️ Signed URL failed, trying direct fallback approach...');
        
        // Update loading message
        if (loadingToast) {
          loadingToast.textContent = '🔄 Trying alternative method...';
        }
        
        // Fallback: Try direct URL conversion
        if (record.photoUrl.startsWith('gs://')) {
          const gsUrl = record.photoUrl.replace('gs://', '');
          const [bucket, ...pathParts] = gsUrl.split('/');
          photoUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(pathParts.join('/'))}?alt=media`;
          console.log('🔄 Using direct URL fallback:', photoUrl);
        } else {
          photoUrl = record.photoUrl;
        }
        
        // Test if the fallback URL is accessible
        try {
          const response = await fetch(photoUrl, { method: 'HEAD' });
          if (!response.ok) {
            throw new Error(`Direct URL not accessible: ${response.status}`);
          }
          console.log('✅ Direct URL fallback successful');
        } catch (fallbackError) {
          console.error('❌ Both signed URL and direct URL failed:', fallbackError);
          throw new Error('Photo is not accessible through any method');
        }
      }
      
      // Remove loading toast
      if (loadingToast && document.body.contains(loadingToast)) {
        document.body.removeChild(loadingToast);
        loadingToast = null;
      }

      if (photoUrl) {
        onPhotoView && onPhotoView(photoUrl, record);
      } else {
        throw new Error('No photo URL available');
      }
      
    } catch (error) {
      // Remove loading toast if it exists
      if (loadingToast && document.body.contains(loadingToast)) {
        document.body.removeChild(loadingToast);
      }
      
      console.error('❌ Photo loading error:', error);
      
      // Provide user-friendly error messages based on error type
      let errorMessage = 'Unable to load photo. ';
      
      if (error.code === 'functions/permission-denied') {
        errorMessage += 'You do not have permission to view this photo.';
      } else if (error.code === 'functions/not-found') {
        errorMessage += 'The photo could not be found. It may have been deleted.';
      } else if (error.code === 'functions/unauthenticated') {
        errorMessage += 'Please log in again to view this photo.';
      } else if (error.code === 'functions/deadline-exceeded' || error.message?.includes('timeout')) {
        errorMessage += 'The request timed out. Please check your connection and try again.';
      } else if (error.code === 'functions/unavailable' || error.message?.includes('network')) {
        errorMessage += 'Network error occurred. Please check your connection and try again.';
      } else if (error.userMessage) {
        errorMessage += error.userMessage;
      } else {
        errorMessage += 'Please try again or contact support if the problem persists.';
      }
      
      // Show error with retry option for certain error types
      const retryableErrors = [
        'functions/deadline-exceeded',
        'functions/unavailable',
        'functions/internal'
      ];
      
      if (retryableErrors.includes(error.code) || error.message?.includes('timeout') || error.message?.includes('network')) {
        const retry = window.confirm(errorMessage + '\n\nWould you like to try again?');
        if (retry) {
          // Retry after a short delay
          setTimeout(() => handlePhotoClick(record), 1000);
          return;
        }
      } else {
        alert(errorMessage);
      }
    }
  };

  // Handle sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort records
  const sortedRecords = React.useMemo(() => {
    const sortableRecords = [...records];
    sortableRecords.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle special cases
      if (sortConfig.key === 'timestamp' || sortConfig.key === 'createdAt' || sortConfig.key === 'updatedAt') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sortableRecords;
  }, [records, sortConfig]);

  // Format date/time
  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-SG', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format location
  const formatLocation = (location) => {
    if (!location) return 'N/A';
    return `${location.latitude?.toFixed(6) || 'N/A'}, ${location.longitude?.toFixed(6) || 'N/A'}`;
  };

  // Get status display
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'approved':
        return { icon: '✅', text: 'APPROVED', color: '#52c41a', bgColor: '#f6ffed' };
      case 'pending':
        return { icon: '⏳', text: 'PENDING', color: '#faad14', bgColor: '#fffbe6' };
      case 'rejected':
        return { icon: '❌', text: 'REJECTED', color: '#ff4d4f', bgColor: '#fff2f0' };
      default:
        return { icon: '❓', text: 'UNKNOWN', color: '#d9d9d9', bgColor: '#fafafa' };
    }
  };

  // Get type display
  const getTypeDisplay = (type) => {
    return type === 'clock-in' 
      ? { icon: '🟢', text: 'IN', color: '#52c41a' }
      : { icon: '🔴', text: 'OUT', color: '#ff4d4f' };
  };

  // Get location display
  const getLocationDisplay = (isWarehouse) => {
    return isWarehouse 
      ? { icon: '🏢', text: 'Warehouse', color: '#1890ff' }
      : { icon: '🌍', text: 'Job-site', color: '#722ed1' };
  };

  // Get validation score color
  const getValidationScoreColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#ff4d4f';
  };

  // Get sort icon
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return '↕️';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (records.length === 0) {
    return (
      <div className="no-records">
        <div className="no-records-icon">📋</div>
        <h3>No Attendance Records Found</h3>
        <p>No records match the current filter criteria. Try adjusting your filters.</p>
      </div>
    );
  }

  return (
    <div className="attendance-table">
      {/* Table Header Info */}
      <div className="table-info">
        <span>
          Showing {((currentPage - 1) * recordsPerPage) + 1}-{Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} records
        </span>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="attendance-data-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th 
                className="sortable-header"
                onClick={() => handleSort('employeeName')}
              >
                Employee {getSortIcon('employeeName')}
              </th>
              <th 
                className="sortable-header"
                onClick={() => handleSort('type')}
              >
                Type {getSortIcon('type')}
              </th>
              <th 
                className="sortable-header"
                onClick={() => handleSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
              <th 
                className="sortable-header"
                onClick={() => handleSort('timestamp')}
              >
                Time {getSortIcon('timestamp')}
              </th>
              <th 
                className="sortable-header"
                onClick={() => handleSort('isWarehouse')}
              >
                Location {getSortIcon('isWarehouse')}
              </th>
              <th>Score</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRecords.map((record) => {
              const statusDisplay = getStatusDisplay(record.status);
              const typeDisplay = getTypeDisplay(record.type);
              const locationDisplay = getLocationDisplay(record.isWarehouse);
              const isExpanded = expandedRows.has(record.id);
              const isUpdating = updating.has(record.id);

              return (
                <React.Fragment key={record.id}>
                  {/* Main Row */}
                  <tr className={`table-row ${isExpanded ? 'expanded' : ''}`}>
                    <td className="photo-cell">
                      {record.photoUrl ? (
                        <div className="photo-thumbnail-container">
                          <img
                            src={record.photoUrl.startsWith('gs://') ? 
                              `https://storage.googleapis.com/${record.photoUrl.replace('gs://', '')}` : 
                              record.photoUrl}
                            alt="Attendance"
                            className="photo-thumbnail"
                            onClick={() => handlePhotoClick(record)}
                            loading="lazy"
                            onError={(e) => {
                              // Hide the broken image and show clickable icon instead
                              e.target.style.display = 'none';
                              const container = e.target.parentElement;
                              if (container && !container.querySelector('.photo-error-icon')) {
                                const errorIcon = document.createElement('div');
                                errorIcon.className = 'photo-error-icon';
                                errorIcon.innerHTML = '📷';
                                errorIcon.title = 'Click to view photo';
                                errorIcon.onclick = () => handlePhotoClick(record);
                                container.appendChild(errorIcon);
                              }
                            }}
                          />
                          <div className="photo-overlay" onClick={() => handlePhotoClick(record)}>
                            📷
                          </div>
                        </div>
                      ) : (
                        <div className="no-photo-thumbnail clickable-photo-icon" onClick={() => handlePhotoClick(record)} title="Click to view photo">
                          📷
                        </div>
                      )}
                    </td>
                    <td className="employee-cell">
                      <div className="employee-info">
                        <span className="employee-name">{record.employeeName || 'Unknown'}</span>
                        <span className="employee-id">ID: {record.employeeId || record.uid}</span>
                      </div>
                    </td>
                    <td className="type-cell">
                      <span 
                        className="type-badge"
                        style={{ color: typeDisplay.color }}
                      >
                        {typeDisplay.icon} {typeDisplay.text}
                      </span>
                    </td>
                    <td className="status-cell">
                      <span 
                        className="status-badge"
                        style={{ 
                          color: statusDisplay.color,
                          backgroundColor: statusDisplay.bgColor,
                          border: `1px solid ${statusDisplay.color}20`
                        }}
                      >
                        {statusDisplay.icon} {statusDisplay.text}
                      </span>
                    </td>
                    <td className="time-cell">
                      {formatDateTime(record.timestamp)}
                    </td>
                    <td className="location-cell">
                      <span 
                        className="location-badge"
                        style={{ color: locationDisplay.color }}
                      >
                        {locationDisplay.icon} {locationDisplay.text}
                      </span>
                    </td>
                    <td className="score-cell">
                      <span 
                        className="validation-score"
                        style={{ color: getValidationScoreColor(record.validationResults?.overallScore || 0) }}
                      >
                        {record.validationResults?.overallScore || 0}%
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="expand-btn"
                        onClick={() => toggleRowExpansion(record.id)}
                        title={isExpanded ? 'Hide Details' : 'Show Details'}
                      >
                        {isExpanded ? '▲' : '▼'}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {isExpanded && (
                    <tr className="expanded-row">
                      <td colSpan="8">
                        <div className="expanded-content">
                          {/* Details Grid */}
                          <div className="details-grid">
                            <div className="detail-section">
                              <h4>📍 Location Details</h4>
                              <div className="detail-item">
                                <label>GPS Coordinates:</label>
                                <span>{formatLocation(record.location)}</span>
                              </div>
                              <div className="detail-item">
                                <label>GPS Accuracy:</label>
                                <span>{record.location?.accuracy ? `${record.location.accuracy}m` : 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Altitude:</label>
                                <span>{record.location?.altitude ? `${record.location.altitude}m` : 'N/A'}</span>
                              </div>
                            </div>

                            <div className="detail-section">
                              <h4>✅ Validation Results</h4>
                              {record.validationResults && (
                                <>
                                  <div className="validation-checks">
                                    <div className={`validation-check ${record.validationResults.photoRecency ? 'pass' : 'fail'}`}>
                                      {record.validationResults.photoRecency ? '✅' : '❌'} Photo Recency
                                    </div>
                                    <div className={`validation-check ${record.validationResults.gpsAccuracy ? 'pass' : 'fail'}`}>
                                      {record.validationResults.gpsAccuracy ? '✅' : '❌'} GPS Accuracy
                                    </div>
                                    <div className={`validation-check ${record.validationResults.warehouseProximity ? 'pass' : 'fail'}`}>
                                      {record.validationResults.warehouseProximity ? '✅' : '❌'} Location Verification
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>

                            <div className="detail-section">
                              <h4>📱 Device Info</h4>
                              <div className="detail-item">
                                <label>Device:</label>
                                <span>{record.photoMetadata?.deviceInfo ? 
                                  `${record.photoMetadata.deviceInfo.platform || 'Unknown'} - ${record.photoMetadata.deviceInfo.userAgent?.substring(0, 50) || 'Unknown'}...` : 
                                  'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Photo Capture:</label>
                                <span>{record.photoMetadata?.captureTime ? formatDateTime(record.photoMetadata.captureTime) : 'N/A'}</span>
                              </div>
                              <div className="detail-item">
                                <label>Live Capture:</label>
                                <span>{record.photoMetadata?.isLiveCapture ? '✅ Yes' : '❌ No'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Notes */}
                          {(record.notes || record.adminNotes) && (
                            <div className="notes-section">
                              {record.notes && (
                                <div className="note-item">
                                  <label>Employee Notes:</label>
                                  <span>{record.notes}</span>
                                </div>
                              )}
                              {record.adminNotes && (
                                <div className="note-item">
                                  <label>Admin Notes:</label>
                                  <span>{record.adminNotes}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="expanded-actions">
                            {record.status !== 'approved' && (
                              <button
                                className="action-btn approve-btn"
                                onClick={() => handleStatusUpdate(record.id, 'approved')}
                                disabled={isUpdating}
                              >
                                {isUpdating ? '⏳' : '✅'} Approve
                              </button>
                            )}
                            
                            {record.status !== 'rejected' && (
                              <button
                                className="action-btn reject-btn"
                                onClick={() => handleStatusUpdate(record.id, 'rejected')}
                                disabled={isUpdating}
                              >
                                {isUpdating ? '⏳' : '❌'} Reject
                              </button>
                            )}
                            
                            {record.status !== 'pending' && (
                              <button
                                className="action-btn pending-btn"
                                onClick={() => handleStatusUpdate(record.id, 'pending')}
                                disabled={isUpdating}
                              >
                                {isUpdating ? '⏳' : '⏳'} Pending
                              </button>
                            )}

                            {record.photoUrl && (
                              <button
                                className="action-btn photo-btn"
                                onClick={() => handlePhotoClick(record)}
                              >
                                📷 View Photo
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceTable;
